import { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { DataProtectionNotice } from "@/components/DataProtectionNotice";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Progress } from "@/components/ui/progress";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/translations";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

const enrollmentSchema = z.object({
  parent_name: z.string().trim().min(2, { message: "Name must be at least 2 characters" }).max(100),
  parent_email: z.string().trim().email({ message: "Invalid email address" }).max(255),
  parent_phone: z.string().trim().min(9, { message: "Phone number must be at least 9 digits" }).max(20),
  children: z.array(z.object({
    name: z.string().trim().min(2).max(100),
    birthDate: z.string().min(1, { message: "Birth date is required" }),
    currentSchool: z.string().trim().max(200).optional(),
    level: z.enum(["infantil", "primary-c1", "primary-c2"])
  })).min(1, { message: "At least one child is required" }),
  preferred_start_date: z.string().min(1, { message: "Please select a start date" }),
  how_did_you_hear: z.string().optional(),
  transport: z.boolean().default(false),
  early_birds: z.boolean().default(false),
  lunch: z.boolean().default(false),
  comments: z.string().trim().max(1000).optional(),
  privacy_accepted: z.boolean().refine(val => val === true, { message: "You must accept the privacy policy" })
});

type EnrollmentFormData = z.infer<typeof enrollmentSchema>;

const TOTAL_STEPS = 5;

const STEP_FIELDS: Record<number, (string | ((childCount: number) => string[]))[]> = {
  0: ["parent_name", "parent_email", "parent_phone"],
  1: [(childCount: number) => {
    const fields: string[] = [];
    for (let i = 0; i < childCount; i++) {
      fields.push(`children.${i}.name`, `children.${i}.birthDate`, `children.${i}.level`);
    }
    return fields;
  }],
  2: ["preferred_start_date"],
  3: [],
  4: ["privacy_accepted"],
};

function getStepFieldNames(step: number, childCount: number): string[] {
  const defs = STEP_FIELDS[step] || [];
  const names: string[] = [];
  for (const def of defs) {
    if (typeof def === "function") {
      names.push(...def(childCount));
    } else {
      names.push(def);
    }
  }
  return names;
}

const stepKeys = ["contact", "children", "preferences", "services", "review"] as const;

interface EnrollmentRequestFormProps {
  title?: string;
  subtitle?: string;
  source?: string;
}

export const EnrollmentRequestForm = ({ title, subtitle, source }: EnrollmentRequestFormProps = {}) => {
  const { language } = useLanguage();
  const t = translations[language].enrollmentForm;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const navigate = useNavigate();

  const confirmationPaths = {
    es: '/es/confirmacion-preinscripcion',
    en: '/en/enrollment-confirmation',
    pt: '/pt/confirmacao-preinscricao',
    fr: '/fr/confirmation-preinscription'
  };

  const form = useForm<EnrollmentFormData>({
    resolver: zodResolver(enrollmentSchema),
    defaultValues: {
      parent_name: "",
      parent_email: "",
      parent_phone: "",
      children: [{ name: "", birthDate: "", currentSchool: "", level: "infantil" }],
      preferred_start_date: "",
      how_did_you_hear: source || "",
      transport: false,
      early_birds: false,
      lunch: false,
      comments: "",
      privacy_accepted: false
    }
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "children"
  });

  const handleNext = async () => {
    const fieldNames = getStepFieldNames(currentStep, fields.length);
    if (fieldNames.length > 0) {
      const valid = await form.trigger(fieldNames as any);
      if (!valid) return;
    }
    setCompletedSteps(prev => new Set(prev).add(currentStep));
    setCurrentStep(s => Math.min(s + 1, TOTAL_STEPS - 1));
  };

  const handlePrevious = () => {
    setCurrentStep(s => Math.max(s - 1, 0));
  };

  const onSubmit = async (data: EnrollmentFormData) => {
    setIsSubmitting(true);

    try {
      // The edge function inserts the row and sends both emails atomically.
      // If the admin email fails, it rolls back the insert so retries don't duplicate.
      const { error: invokeError } = await supabase.functions.invoke("send-enrollment-request", {
        body: {
          ...data,
          language: language
        }
      });

      if (invokeError) throw invokeError;

      toast.success(t.success);
      form.reset();
      window.location.href = confirmationPaths[language];
    } catch (error: any) {
      console.error("Enrollment request error:", error);
      toast.error(t.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const progressValue = ((currentStep + 1) / TOTAL_STEPS) * 100;

  const renderStepper = () => (
    <div className="mb-8">
      {/* Step circles */}
      <div className="flex items-center justify-between mb-3">
        {stepKeys.map((key, index) => (
          <div key={key} className="flex flex-col items-center flex-1">
            <div className="flex items-center w-full">
              {index > 0 && (
                <div
                  className={`h-0.5 flex-1 transition-colors duration-300 ${
                    index <= currentStep ? "bg-accent" : "bg-border"
                  }`}
                />
              )}
              <button
                type="button"
                onClick={() => {
                  if (completedSteps.has(index) || index < currentStep) {
                    setCurrentStep(index);
                  }
                }}
                className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 flex-shrink-0 ${
                  index === currentStep
                    ? "bg-accent text-accent-foreground shadow-md scale-110"
                    : completedSteps.has(index)
                    ? "bg-accent/20 text-primary cursor-pointer hover:bg-accent/30"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {completedSteps.has(index) && index !== currentStep ? (
                  <Check className="w-4 h-4" />
                ) : (
                  index + 1
                )}
              </button>
              {index < stepKeys.length - 1 && (
                <div
                  className={`h-0.5 flex-1 transition-colors duration-300 ${
                    index < currentStep ? "bg-accent" : "bg-border"
                  }`}
                />
              )}
            </div>
            <span
              className={`mt-2 text-xs text-center hidden md:block transition-colors duration-300 ${
                index === currentStep
                  ? "text-primary font-medium"
                  : completedSteps.has(index)
                  ? "text-primary/70"
                  : "text-muted-foreground"
              }`}
            >
              {t.stepper.steps[key]}
            </span>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      <Progress value={progressValue} className="h-1.5" />

      {/* Mobile step label */}
      <p className="text-sm text-muted-foreground text-center mt-2 md:hidden">
        {t.stepper.stepOf
          .replace("{current}", String(currentStep + 1))
          .replace("{total}", String(TOTAL_STEPS))}
        {" — "}
        {t.stepper.steps[stepKeys[currentStep]]}
      </p>
    </div>
  );

  const renderStep0 = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-medium text-primary">{t.contactInfo}</h3>

      <FormField
        control={form.control}
        name="parent_name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t.parentName}</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="parent_email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t.email}</FormLabel>
              <FormControl>
                <Input type="email" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="parent_phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t.phone}</FormLabel>
              <FormControl>
                <Input type="tel" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-medium text-primary">{t.childrenInfo}</h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => append({ name: "", birthDate: "", currentSchool: "", level: "infantil" })}
        >
          <Plus className="w-4 h-4 mr-2" />
          {t.addChild}
        </Button>
      </div>

      {fields.map((field, index) => (
        <div key={field.id} className="p-4 border border-border rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-primary">{t.child} {index + 1}</h4>
            {fields.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => remove(index)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>

          <FormField
            control={form.control}
            name={`children.${index}.name`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t.childName}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name={`children.${index}.birthDate`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t.birthDate}</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={`children.${index}.level`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t.desiredLevel}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="infantil">{t.levels.infantil}</SelectItem>
                      <SelectItem value="primary-c1">{t.levels.primaryC1}</SelectItem>
                      <SelectItem value="primary-c2">{t.levels.primaryC2}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name={`children.${index}.currentSchool`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t.currentSchool}</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={t.currentSchoolPlaceholder} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      ))}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-medium text-primary">{t.preferences}</h3>

      <div className={source ? "" : "grid md:grid-cols-2 gap-4"}>
        <FormField
          control={form.control}
          name="preferred_start_date"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t.preferredStartDate}</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="2026-2027">2026-2027</SelectItem>
                  <SelectItem value="2027-2028">2027-2028</SelectItem>
                  <SelectItem value="immediate">{t.immediate}</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {!source && (
          <FormField
            control={form.control}
            name="how_did_you_hear"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t.howDidYouHear}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="web">{t.sources.web}</SelectItem>
                    <SelectItem value="recommendation">{t.sources.recommendation}</SelectItem>
                    <SelectItem value="social-media">{t.sources.socialMedia}</SelectItem>
                    <SelectItem value="open-house">{t.sources.openHouse}</SelectItem>
                    <SelectItem value="other">{t.sources.other}</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-medium text-primary">{t.servicesInterest}</h3>

      <div className="grid md:grid-cols-3 gap-4">
        <FormField
          control={form.control}
          name="transport"
          render={({ field }) => (
            <FormItem className="flex items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>{t.services.transport}</FormLabel>
              </div>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="early_birds"
          render={({ field }) => (
            <FormItem className="flex items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>{t.services.earlyBirds}</FormLabel>
              </div>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="lunch"
          render={({ field }) => (
            <FormItem className="flex items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>{t.services.lunch}</FormLabel>
              </div>
            </FormItem>
          )}
        />
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="comments"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t.comments}</FormLabel>
            <FormControl>
              <Textarea {...field} rows={4} placeholder={t.commentsPlaceholder} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="privacy_accepted"
        render={({ field }) => (
          <FormItem className="flex items-start space-x-3 space-y-0">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>{t.privacyPolicy}</FormLabel>
              <FormMessage />
            </div>
          </FormItem>
        )}
      />

      <DataProtectionNotice finalidad="plaza" />
    </div>
  );

  const stepRenderers = [renderStep0, renderStep1, renderStep2, renderStep3, renderStep4];

  return (
    <div className="bg-card rounded-2xl p-8 shadow-lg">
      <h2 className="text-3xl font-light text-primary mb-2">{title ?? t.title}</h2>
      <p className="text-muted-foreground mb-8">{subtitle ?? t.subtitle}</p>

      {renderStepper()}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }}
            >
              {stepRenderers[currentStep]()}
            </motion.div>
          </AnimatePresence>

          {/* Navigation buttons */}
          <div className="flex justify-between mt-8">
            {currentStep > 0 ? (
              <Button
                type="button"
                variant="outline"
                onClick={handlePrevious}
              >
                {t.stepper.previous}
              </Button>
            ) : (
              <div />
            )}

            {currentStep < TOTAL_STEPS - 1 ? (
              <Button
                type="button"
                onClick={handleNext}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {t.stepper.next}
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full ml-4 bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t.submitting}
                  </>
                ) : (
                  t.submit
                )}
              </Button>
            )}
          </div>
        </form>
      </Form>
    </div>
  );
};
